/**
 * Ore chunks (M2 plan §6 M2-20): one `InstancedMesh` of the `prop.ore_chunk` geometry for the whole chunk pool,
 * with a per-instance colour from the ware's tint (`wares.json tint`, linear), a size from the chunk's tonnes
 * (`scalePerCubeRootTonne × ∛t`, jittered by its seed) and a seed-derived tumble. Instance matrices are in render
 * space (the mesh sits at the render origin, D-011), written straight into the instance buffers each frame.
 * The mesh is built once the deferred prop has loaded; `frameChunks` is allocation-free (D-014).
 */
import { DynamicDrawUsage, InstancedBufferAttribute, InstancedMesh, MeshStandardNodeMaterial, type BufferGeometry, type Mesh, type Object3D } from 'three/webgpu'
import type { LoadedModel } from '../../assets/loader'
import type { Quat, Vec3 } from '../../core/math'
import { WARE_COUNT, type WaresData } from '../../data/m2Schemas'
import type { ChunkPool } from '../../space/contracts'
import { linearColor, type ViewsConfig } from '../spacefx/config'
import { seedRange, seedSpin, writeTRS } from './transform'

export const ORE_CHUNK_ASSET = 'prop.ore_chunk'

export interface ChunkView {
  /** Linear RGB per ware index (3 floats each). */
  readonly colors: Float32Array
  readonly capacity: number
  /** Null until the prop has loaded. */
  mesh: InstancedMesh | null
  /** Instances drawn last frame. */
  drawn: number
}

export function createChunkView(wares: Pick<WaresData, 'wares'>, capacity: number): ChunkView {
  const colors = new Float32Array(WARE_COUNT * 3)
  for (let i = 0; i < WARE_COUNT; i++) linearColor(wares.wares[i].tintHex, 1, colors, i * 3)
  return { colors, capacity, mesh: null, drawn: 0 }
}

/** The linear colour of ware `ware` into `out` ([r, g, b]). */
export function chunkColor(view: ChunkView, ware: number, out: Float32Array): Float32Array {
  const o = ware * 3
  out[0] = view.colors[o]
  out[1] = view.colors[o + 1]
  out[2] = view.colors[o + 2]
  return out
}

/** The geometry of the model's most detailed level (its first mesh). */
function chunkGeometry(model: LoadedModel): BufferGeometry | null {
  const inst = model.instantiate(1)
  const level = inst.lod.levels[0]?.object ?? inst.root
  let found: BufferGeometry | null = null
  level.traverse((o) => {
    const m = o as Mesh
    if (!found && m.isMesh) found = m.geometry
  })
  return found
}

/** Builds the instanced mesh once `prop.ore_chunk` is loaded (allocates, once). True when built. */
export function buildChunkMesh(view: ChunkView, models: ReadonlyMap<string, LoadedModel>, cfg: ViewsConfig, parent: Object3D): boolean {
  if (view.mesh) return false
  const model = models.get(ORE_CHUNK_ASSET)
  if (!model) return false
  const geometry = chunkGeometry(model)
  if (!geometry) return false
  const material = new MeshStandardNodeMaterial({ roughness: cfg.chunk.roughness, metalness: cfg.chunk.metalness })
  material.name = 'm2:oreChunk'
  const cap = Math.max(1, view.capacity)
  const mesh = new InstancedMesh(geometry, material, cap)
  mesh.name = 'm2:oreChunks'
  mesh.instanceMatrix.setUsage(DynamicDrawUsage)
  const color = new InstancedBufferAttribute(new Float32Array(cap * 3), 3)
  color.setUsage(DynamicDrawUsage)
  mesh.instanceColor = color
  mesh.count = 0
  mesh.visible = false
  mesh.frustumCulled = false
  mesh.matrixAutoUpdate = false
  // PERF PF-4 (D-771): ore chunks only receive. Tiny and unculled, as casters they drew in every shadow level for a sub-pixel shadow.
  mesh.castShadow = false
  mesh.receiveShadow = true
  parent.add(mesh)
  view.mesh = mesh
  return true
}

const q: Quat = { x: 0, y: 0, z: 0, w: 1 }

/**
 * Writes every live chunk: render-space position `back` seconds along its velocity, ware colour, tonnage scale and
 * seed tumble at render time `time`. Allocation-free.
 */
export function frameChunks(view: ChunkView, chunks: ChunkPool, cfg: ViewsConfig, time: number, back: number, cam: Vec3): void {
  const mesh = view.mesh
  if (!mesh) {
    view.drawn = 0
    return
  }
  const m = mesh.instanceMatrix.array as Float32Array
  const col = mesh.instanceColor ? (mesh.instanceColor.array as Float32Array) : null
  const c = cfg.chunk
  const high = chunks.high < chunks.capacity ? chunks.high : chunks.capacity
  let n = 0
  for (let i = 0; i < high && n < view.capacity; i++) {
    if (chunks.alive[i] !== 1) continue
    const seed = chunks.seed[i]
    const t = chunks.tonnes[i]
    const scale = c.scalePerCubeRootTonne * Math.cbrt(t > 0 ? t : 0) * (1 + c.scaleJitter * (2 * seedRange(seed, 0, 1) - 1))
    seedSpin(seed, time * seedRange(seed, c.spinRate[0], c.spinRate[1]), q)
    writeTRS(
      m,
      n * 16,
      Math.fround(chunks.x[i] + chunks.vx[i] * back - cam.x),
      Math.fround(chunks.y[i] + chunks.vy[i] * back - cam.y),
      Math.fround(chunks.z[i] + chunks.vz[i] * back - cam.z),
      q.x,
      q.y,
      q.z,
      q.w,
      scale,
      scale,
      scale,
    )
    if (col) {
      const w = chunks.ware[i] * 3
      col[n * 3] = view.colors[w]
      col[n * 3 + 1] = view.colors[w + 1]
      col[n * 3 + 2] = view.colors[w + 2]
    }
    n++
  }
  mesh.count = n
  mesh.visible = n > 0
  view.drawn = n
  if (n === 0) return
  mesh.instanceMatrix.needsUpdate = true
  if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true
}

export function disposeChunkView(view: ChunkView): void {
  const mesh = view.mesh
  if (!mesh) return
  mesh.removeFromParent()
  ;(mesh.material as MeshStandardNodeMaterial).dispose()
  mesh.dispose()
  view.mesh = null
}
